import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Flex,
  Popconfirm,
  Tag,
  Typography,
  message,
  Collapse,
} from "antd";
import { CheckCircleOutlined, StopOutlined } from "@ant-design/icons";
import pallete from "../../utils/pallete";
import StatusTag from "../../components/StatusTag";
import { useGetDetailApi } from "../../hooks/useApi";
import { institutionalVerificationService } from "../../services/institutional-verification.service";
import CertificateUploader from "../../components/CertificateUploader";
import CertificateInfo from "../../components/CertificateInfo";
import { fileService } from "../../services/file.service";
import { useMe } from "../../hooks/useMe";
import { MODAL_MESSAGES } from "./constants";
import LegalitySection from "./components/LegalitySection";
import FacilitySection from "./components/FacilitySection";
import ProgramSection from "./components/ProgramSection";
import ActivationSection from "./components/ActivationSection";
import MonitoringSection from "./components/MonitoringSection";
import VerificationModals from "./components/VerificationModals";
import { canVerify } from "./authorization";

export default function InstitutionalVerificationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [messageApi, contextHolder] = message.useMessage();
  const [previewDoc, setPreviewDoc] = useState("");
  const [confirmVerify, setConfirmVerify] = useState(false);
  const [confirmRevise, setConfirmRevise] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);
  const [reviseNote, setReviseNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [verificationType, setVerificationType] = useState(null);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    keyword: null,
  });
  const { data: userDataProfile } = useMe();
  const userPosition = userDataProfile?.data?.position?.toLowerCase();
  const userDirectorate = userDataProfile?.data?.directorate;

  const { data: legalityData } = useGetDetailApi({
    id,
    serviceFn: institutionalVerificationService.fetchLegalityData,
    queryKey: ["legality-data"],
  });

  const { data: facilityData } = useGetDetailApi({
    id,
    serviceFn: institutionalVerificationService.fetchFacilityData,
    queryKey: ["facility-data"],
  });

  const { data: certificateData } = useGetDetailApi({
    id,
    serviceFn: institutionalVerificationService.fetchCertificateData,
    queryKey: ["certificate-data"],
  });

  const isAllowedLegality = canVerify({
    userPosition,
    userDirectorate,
    status: legalityData?.status,
  });

  const isAllowedFacility = canVerify({
    userPosition,
    userDirectorate,
    status: facilityData?.status,
  });

  const {
    data: programData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["program-data"],
    queryFn: () => institutionalVerificationService.fetchAllProgramData(id),
    enabled: !!id,
    select: (data) => data,
  });

  const descriptionTitle = (title) => (
    <Typography.Title
      level={3}
      style={{
        marginBottom: 0,
        fontWeight: "bold",
        fontSize: 18,
      }}
    >
      {title}
    </Typography.Title>
  );

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page,
    });
  };

  const { mutate: handleVerifyMutation, isPending: isVerifying } = useMutation({
    mutationFn: (type) => {
      const payload = { status: "accepted" };

      if (type === "facility") {
        return institutionalVerificationService.updateFacilityStatusData(
          id,
          payload,
        );
      }

      return institutionalVerificationService.updateRegistrationStatusData(
        id,
        payload,
      );
    },

    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.VERIFY.SUCCESS);
      setConfirmVerify(false);
      queryClient.invalidateQueries(["institutional-verification"]);
      setTimeout(() => navigate("/institutional-verification"), 1500);
    },

    onError: (error) => {
      console.error("Verify error:", error);
      messageApi.error(MODAL_MESSAGES.VERIFY.ERROR);
    },
  });

  const { mutate: handleRejectMutation, isPending: isRejecting } = useMutation({
    mutationFn: (reason) => {
      const payload = { status: "rejected", reason: reason };

      if (verificationType === "facility") {
        return institutionalVerificationService.updateFacilityStatusData(
          id,
          payload,
        );
      }

      return institutionalVerificationService.updateRegistrationStatusData(
        id,
        payload,
      );
    },

    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.REJECT.SUCCESS);
      setRejectReason("");
      setConfirmReject(false);

      queryClient.invalidateQueries(["institutional-verification"]);
      setTimeout(() => navigate("/institutional-verification"), 1500);
    },

    onError: (error) => {
      console.error("Reject error:", error);
      messageApi.error(MODAL_MESSAGES.REJECT.ERROR);
    },
  });

  const { mutate: handleReviseMutation, isPending: isRevising } = useMutation({
    mutationFn: (reason) => {
      const payload = { status: "revised", reason: reason };

      if (verificationType === "facility") {
        return institutionalVerificationService.updateFacilityStatusData(
          id,
          payload,
        );
      }

      return institutionalVerificationService.updateRegistrationStatusData(
        id,
        payload,
      );
    },

    onSuccess: () => {
      messageApi.success(MODAL_MESSAGES.REVISE.SUCCESS);
      setReviseNote("");
      setConfirmRevise(false);

      queryClient.invalidateQueries(["institutional-verification"]);
      setTimeout(() => navigate("/institutional-verification"), 1500);
    },

    onError: (error) => {
      console.error("Revise error:", error);
      messageApi.error(MODAL_MESSAGES.REVISE.ERROR);
    },
  });

  const isActive = legalityData?.isActive ?? true;

  const isAllowedChangeStatus =
    userPosition === "superadmin" ||
    userPosition === "admin" ||
    userPosition === "direktur" ||
    userPosition === "verifikator" ||
    userPosition === undefined;

  const {
    mutate: handleToggleActiveMutation,
    isPending: isUpdatingActiveStatus,
  } = useMutation({
    mutationFn: (newStatus) => {
      return institutionalVerificationService.updateActiveStatus(id, {
        isActive: newStatus,
      });
    },
    onSuccess: (response, newStatus) => {
      messageApi.success(
        response?.message ||
          `Status lembaga berhasil diubah menjadi ${
            newStatus ? "aktif" : "inaktif"
          }`,
      );
      queryClient.invalidateQueries({ queryKey: ["legality-data"] });
      queryClient.invalidateQueries({
        queryKey: ["institutional-verification"],
      });
    },
    onError: (error) => {
      console.error("Update active status error:", error);
      messageApi.error(
        error?.response?.data?.message || "Gagal mengubah status lembaga",
      );
    },
  });

  const handleVerifyButton = (type) => {
    setVerificationType(type);
    setConfirmVerify(true);
  };

  const handleRejectionButton = (type) => {
    setVerificationType(type);
    setConfirmReject(true);
  };

  const handleReviseButton = (type) => {
    setVerificationType(type);
    setConfirmRevise(true);
  };

  const extractUUID = (filePath) => {
    if (!filePath) return null;
    const match = filePath.match(
      /[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/,
    );
    return match ? match[0] : null;
  };

  const validateSafeContent = async (blob) => {
    try {
      const slice = blob.slice(0, 12);
      const buffer = await slice.arrayBuffer();
      const view = new Uint8Array(buffer);
      const headerHex = Array.from(view.slice(0, 4))
        .map((b) => b.toString(16).padStart(2, "0").toUpperCase())
        .join("");
      const headerStr = Array.from(view)
        .map((b) => String.fromCharCode(b))
        .join("");

      if (headerHex.startsWith("89504E47")) return "image/png";
      if (headerHex.startsWith("FFD8")) return "image/jpeg";
      if (headerHex.startsWith("47494638")) return "image/gif";
      if (headerStr.startsWith("RIFF") && headerStr.substring(8, 12) === "WEBP")
        return "image/webp";
      if (headerStr.startsWith("%PDF")) return "application/pdf";

      return null;
    } catch (e) {
      return null;
    }
  };

  const handlePreview = async (filePath) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Memuat dokumen...", key: "preview" });
      const blob = await fileService.getFile(uuid);

      // Sanitasi: Periksa konten file (magic bytes) untuk memastikan file benar-benar gambar atau PDF yang valid
      const validMimeType = await validateSafeContent(blob);
      if (!validMimeType) {
        messageApi.error({
          content:
            "Preview ditolak: Format file tidak didukung atau file berpotensi berbahaya. Hanya Gambar dan PDF yang diizinkan.",
          key: "preview",
          duration: 4,
        });
        return;
      }

      // Buat blob baru dengan MIME type yang divalidasi secara ketat untuk mencegah serangan XSS melalui spoofing Content-Type
      const safeBlob = new Blob([blob], { type: validMimeType });
      const url = URL.createObjectURL(safeBlob);
      setPreviewDoc(url);
      messageApi.success({
        content: "Dokumen berhasil dimuat",
        key: "preview",
        duration: 2,
      });
    } catch (error) {
      console.error("Preview error:", error);
      messageApi.error({
        content: "Gagal memuat dokumen",
        key: "preview",
        duration: 2,
      });
    }
  };

  const handleDownloadFile = async (filePath, fileName) => {
    const uuid = extractUUID(filePath);
    if (!uuid) {
      messageApi.error("UUID file tidak ditemukan");
      return;
    }

    try {
      messageApi.loading({ content: "Mengunduh dokumen...", key: "download" });
      const blob = await fileService.getFile(uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      messageApi.success({
        content: "Dokumen berhasil diunduh",
        key: "download",
        duration: 2,
      });
    } catch (error) {
      console.error("Download error:", error);
      messageApi.error({
        content: "Gagal mengunduh dokumen",
        key: "download",
        duration: 2,
      });
    }
  };

  const legalityVerification = legalityData?.verificationStatusses
    ?.filter((v) => v.type === "legality")
    ?.sort((a, b) => new Date(a.time) - new Date(b.time))
    ?.at(-1);

  const facilityVerification = facilityData?.verificationStatusses
    ?.filter((v) => v.type === "facility")
    ?.sort((a, b) => new Date(a.time) - new Date(b.time))
    ?.at(-1);

  const hasCertificate = Boolean(
    certificateData?.filePath && certificateData?.fileName,
  );

  const isCertificateUploadDisabled =
    facilityData == null || !(programData?.data?.length > 0);

  return (
    <>
      {contextHolder}
      <Card title="Detail Lembaga">
        <Collapse
          accordion
          style={{ marginBottom: 20 }}
          defaultActiveKey={["1"]}
          items={[
            {
              key: "1",
              label: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {descriptionTitle("Informasi Legalitas Lembaga")}
                  <StatusTag status={legalityVerification?.status} />
                </div>
              ),
              children: (
                <LegalitySection
                  legalityData={legalityData}
                  legalityVerification={legalityVerification}
                  isVerifying={isVerifying}
                  isRejecting={isRejecting}
                  isRevising={isRevising}
                  isAllowedAction={isAllowedLegality}
                  onVerify={() => setConfirmVerify(true)}
                  onReject={() => setConfirmReject(true)}
                  onPreview={handlePreview}
                  onDownload={handleDownloadFile}
                  onCancel={() => navigate("/institutional-verification")}
                />
              ),
            },
          ]}
        />

        {facilityData != null && (
          <Collapse
            accordion
            style={{ marginBottom: 20 }}
            defaultActiveKey={["1"]}
            items={[
              {
                key: "1",
                label: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {descriptionTitle("Informasi Fasilitas Lembaga")}
                    <StatusTag status={facilityVerification?.status} />
                  </div>
                ),
                children: (
                  <FacilitySection
                    legalityData={legalityData}
                    facilityData={facilityData}
                    facilityVerification={facilityVerification}
                    isVerifying={isVerifying}
                    isRevising={isRevising}
                    isRejecting={isRejecting}
                    isAllowedAction={isAllowedFacility}
                    onVerify={() => handleVerifyButton("facility")}
                    onRevise={() => handleReviseButton("facility")}
                    onPreview={handlePreview}
                    onDownload={handleDownloadFile}
                  />
                ),
              },
            ]}
          />
        )}

        {facilityData != null && (
          <Collapse
            accordion
            style={{ marginBottom: 20 }}
            defaultActiveKey={["1"]}
            items={[
              {
                key: "1",
                label: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {descriptionTitle("Monitoring Fasilitas")}
                  </div>
                ),
                children: (
                  <MonitoringSection
                    institutionId={id}
                    monitorings={facilityData?.monitorings || []}
                    isAllowedAction={isAllowedFacility}
                    onPreview={handlePreview}
                    onDownload={handleDownloadFile}
                  />
                ),
              },
            ]}
          />
        )}

        {programData != null && (
          <Collapse
            accordion
            defaultActiveKey={["1"]}
            items={[
              {
                key: "1",
                label: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {descriptionTitle("Informasi Program Lembaga")}
                  </div>
                ),
                children: (
                  <ProgramSection
                    id={id}
                    programData={programData}
                    isLoading={isLoading}
                    isRefetching={isRefetching}
                    dataParams={dataParams}
                    handleChangePage={handleChangePage}
                  />
                ),
              },
            ]}
          />
        )}

        {legalityData != null && (
          <Collapse
            accordion
            defaultActiveKey={["1"]}
            style={{ marginTop: 20 }}
            items={[
              {
                key: "1",
                label: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    {descriptionTitle("Status Aktivasi Lembaga")}
                  </div>
                ),
                children: (
                  <ActivationSection
                    isActive={isActive}
                    isAllowedAction={isAllowedChangeStatus}
                    isLoading={isUpdatingActiveStatus}
                    onChange={(newStatus) =>
                      handleToggleActiveMutation(newStatus)
                    }
                  />
                ),
              },
            ]}
          />
        )}

        <Collapse
          accordion
          defaultActiveKey={["1"]}
          style={{ marginTop: 20 }}
          items={[
            {
              key: "1",
              label: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {descriptionTitle("Data Sertifikat")}
                </div>
              ),
              children: hasCertificate ? (
                <CertificateInfo
                  data={certificateData}
                  institutionId={id}
                  onPreview={(path) => handlePreview(path)}
                  onDownload={(path, name) => handleDownloadFile(path, name)}
                  messageApi={messageApi}
                  isAllowedAction={isAllowedFacility}
                />
              ) : (
                <Flex vertical gap={8}>
                  {isCertificateUploadDisabled && (
                    <Typography.Text type="danger">
                      * Data fasilitas dan data program harus diisi terlebih
                      dahulu untuk mengupload sertifikat.
                    </Typography.Text>
                  )}
                  <CertificateUploader
                    institutionId={id}
                    messageApi={messageApi}
                    disabled={isCertificateUploadDisabled}
                  />
                </Flex>
              ),
            },
          ]}
        />
      </Card>

      <VerificationModals
        previewDoc={previewDoc}
        setPreviewDoc={setPreviewDoc}
        confirmVerify={confirmVerify}
        setConfirmVerify={setConfirmVerify}
        confirmRevise={confirmRevise}
        setConfirmRevise={setConfirmRevise}
        reviseNote={reviseNote}
        setReviseNote={setReviseNote}
        confirmReject={confirmReject}
        setConfirmReject={setConfirmReject}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        verificationType={verificationType}
        isVerifying={isVerifying}
        isRevising={isRevising}
        isRejecting={isRejecting}
        handleVerifyMutation={handleVerifyMutation}
        handleReviseMutation={handleReviseMutation}
        handleRejectMutation={handleRejectMutation}
      />
    </>
  );
}
